import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, Mail, Loader2, CheckCircle, 
  Send, KeyRound
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Insira o seu email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Email enviado com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar email');
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sonangol-red to-red-700 flex flex-col">
        {/* Header */}
        <div className="pt-8 pb-6 px-4">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg mb-4">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-barlow text-3xl font-bold text-white uppercase tracking-tight">
              Email Enviado!
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-white rounded-t-3xl px-6 py-8">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
              <Mail className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h2 className="font-barlow font-bold text-xl text-green-800 mb-2">
                Verifique o seu email
              </h2>
              <p className="text-green-700 text-sm">
                Enviámos instruções para redefinir a sua palavra-passe para:
              </p>
              <p className="font-bold text-green-800 mt-2">{email}</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-amber-800 text-sm">
                <strong>Não recebeu o email?</strong><br />
                Verifique a pasta de spam ou lixo electrónico.
              </p>
            </div>

            <Button
              variant="outline"
              className="w-full h-12"
              onClick={() => { setSent(false); setEmail(''); }}
              data-testid="resend-btn"
            >
              Tentar com outro email
            </Button>

            <Link 
              to="/auth" 
              className="block mt-4 text-sonangol-red hover:underline font-medium"
            >
              ← Voltar ao login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sonangol-red to-red-700 flex flex-col">
      {/* Header */}
      <div className="pt-6 pb-4 px-4">
        <Link 
          to="/auth" 
          className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Voltar</span>
        </Link>
      </div>

      <div className="px-4 pb-8">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg mb-4">
            <KeyRound className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-barlow text-3xl font-bold text-white uppercase tracking-tight">
            Recuperar Senha
          </h1>
          <p className="text-white/80 text-sm mt-2 font-manrope">
            Insira o seu email para receber instruções
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 bg-white rounded-t-3xl px-6 py-8">
        <div className="max-w-md mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-700 font-medium">
                Email da conta
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  className="h-14 pl-12 bg-zinc-50 border-zinc-200 rounded-xl text-zinc-800 placeholder:text-zinc-400"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <p className="text-xs text-zinc-500">
                Enviaremos um link para redefinir a sua palavra-passe
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full h-14 bg-sonangol-red hover:bg-red-700 font-barlow font-bold uppercase tracking-wider text-lg rounded-xl shadow-lg"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  A enviar...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Enviar Instruções
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-zinc-500 text-sm">
              Lembrou-se da palavra-passe?{' '}
              <Link to="/auth" className="text-sonangol-red hover:underline font-medium">
                Iniciar Sessão
              </Link>
            </p>
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-zinc-50 rounded-xl p-4">
            <h3 className="font-barlow font-bold text-zinc-800 mb-2">Precisa de ajuda?</h3>
            <p className="text-zinc-600 text-sm">
              Se não conseguir aceder à sua conta, entre em contacto com o suporte através do email{' '}
              <a href="mailto:suporte@halubri.sonangol.co.ao" className="text-sonangol-red hover:underline">
                suporte@halubri.sonangol.co.ao
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
